import React, { useContext, useEffect, useMemo, useLayoutEffect, useState } from "react";
import { ng } from "@common/ngds";
import { GetMtrlClr, getSec } from "./DrawMtrlCurve";
import { CreatePolylineFromParams } from "../helpers/auxiliary";

interface SVGComponentProps {
  section: ng.sections.Section_json;
  showAxes?: boolean;
  zoomFactor?: number;
  viewbox?: string;
  rotation?: boolean; // if true, rotVec and rotDeg are required
  rotVec?: [number, number];
  rotDeg?: number;
  setRotDeg?: React.Dispatch<React.SetStateAction<number>>;
  pad?: number;
  hlpNbr?: number;
  grid?: boolean;
}

/** NOTE: there are css animations couple to the id's and classes of these elements.
// The animations are defined in the NGAnimations.scss file
//---now on with the show...
*/

export const SectionSVG = (props: SVGComponentProps) => {
  // --- ui states ---
  const [showAxes, setShowAxes] = useState(false);
  // -- states createPolyLine--
  const [polyLine, setPolyLine] = useState("");
  const [helpPnts, setHelpPnts] = React.useState<Array<[number, number]>>([]);
  const [holes, setHoles] = useState([]);
  const [adds, setAdds] = useState([]);
  const [rotatedPolyLine, setRotatedPolyLine] = useState("");
  const [rotatedHoles, setRotatedHoles] = useState([]);
  const [rotatedAdds, setRotatedAdds] = useState([]);
  const [mtrlFill, setMtrlFill] = useState("#d2d2d5");
  const [zoomFactor, setZoomFactor] = useState(1);
  //--- states calcSecScale---
  const [secH, setSecH] = useState(0);
  const [secW, setSecW] = useState(0);
  const [scale, setScale] = useState(1);

  const w = 400;
  const h = 400;
  const pad = props.pad !== undefined ? props.pad : 0.1;

  const secSVG = {
    w: w,
    h: h,
    origo: { x: w / 2.0, y: h / 2.0 },
    max: { x: w * (1 - 2 * pad), y: h * (1 - 2 * pad) },
    pad: pad,
  };

  const rotSecSVG = {
    w: w / 2,
    h: h / 2,
    origo: { x: w / 4.0, y: h / 4.0 },
    max: { x: (w * (1 - 2 * pad)) / 2, y: (h * (1 - 2 * pad)) / 2 },
    pad: 0.0,
  };

  let hlpNbr = props.hlpNbr ? props.hlpNbr : 0;
  let grid = props.grid ? props.grid : false;

  // ----------------- useEffect -------------------
  useEffect(() => {
    if (props.section) {
      // create polyline
      let secObj = ng.sections.Section.fromObject(props.section);
      createPolyLine(props.section);
      // set color according to material
      setMtrlFill(GetMtrlClr("MATERIAL_CATEGORY_CONCRETE"));
      if (secObj.materialCategory === ng.sections.MaterialCategory.MATERIAL_CATEGORY_STEEL) {
        setMtrlFill(GetMtrlClr("MATERIAL_CATEGORY_STEEL"));
      } else if (secObj.materialCategory === ng.sections.MaterialCategory.MATERIAL_CATEGORY_TIMBER) {
        setMtrlFill(GetMtrlClr("MATERIAL_CATEGORY_TIMBER"));
      }
    }
  }, [scale, props.zoomFactor, props.rotDeg, props.section, pad]);

  useEffect(() => {
    if (props.setRotDeg && props.rotVec) {
      props.setRotDeg(calcRot(props.rotVec));
    }
  }, [props.rotVec, props.setRotDeg]);

  useEffect(() => {
    props.showAxes && setShowAxes(props.showAxes);
  }, [props.showAxes]);

  // ----------------- Functions -------------------

  const createPolyLine = (sec: ng.sections.Section_json) => {
    let secObj = ng.sections.Section.fromObject(sec);
    let sectionToDraw = secObj;

    // Welded sections needs to have their params converted to polylines
    // in a pattern that show parts welded together
    if (
      sectionToDraw.type === ng.sections.SectionType.SECTION_TYPE_HSQ ||
      sectionToDraw.type === ng.sections.SectionType.SECTION_TYPE_UX ||
      sectionToDraw.type === ng.sections.SectionType.SECTION_TYPE_ZX
    ) {
      let tempSec = CreatePolylineFromParams(sectionToDraw);
      sectionToDraw.polylines = tempSec.polylines;
    }

    // Values directly from the database
    let scale = calcSecScale(sectionToDraw, secSVG);
    let zoomFactor = props.zoomFactor ? props.zoomFactor : 1;

    let hlp: Array<[number, number]> = [];
    let prevPnt: number[] = [];

    let firstpoint = "";
    let holes: Array<string> = [];
    let adds: Array<string> = [];

    let points: ng.geometry.IPoint2D[] = sectionToDraw.polylines?.perimeter?.points as ng.geometry.IPoint2D[];
    let per_str = "";
    points.forEach((val, index) => {
      let valX = secSVG.origo.x + val.x * scale * zoomFactor;
      let valY = secSVG.origo.y - val.y * scale * zoomFactor;
      if (index === 0) {
        per_str += `${valX},${valY} `;
        firstpoint = `${valX},${valY} `;
      } else {
        per_str += `${valX},${valY} `;
        hlp.push([(valX + prevPnt[0]) / 2, (valY + prevPnt[1]) / 2]);
      }
      hlp.push([valX, valY]);
      prevPnt = [valX, valY];
    });

    per_str += firstpoint;
    setPolyLine(per_str);

    if (sectionToDraw.polylines!.hasOwnProperty("additionalPerimeters")) {
      // first move perimeter to the beginning of the additional perimeter list and remove it from the 'polyline'-list
      adds.push(per_str);
      setPolyLine("");

      sectionToDraw.polylines!.additionalPerimeters!.forEach((add: ng.geometry.PolyLine2D) => {
        let add_str = "";
        add.points!.forEach((val, hindex) => {
          let valX = secSVG.origo.x + val.x * scale * zoomFactor;
          let valY = secSVG.origo.y - val.y * scale * zoomFactor;
          if (hindex === 0) {
            add_str += ` ${valX},${valY} `;
            firstpoint = `${valX},${valY} `;
          } else {
            add_str += `${valX},${valY} `;
            hlp.push([(valX + prevPnt[0]) / 2, (valY + prevPnt[1]) / 2]);
          }
          hlp.push([valX, valY]);
          prevPnt = [valX, valY];
        });

        add_str += firstpoint;
        adds.push(add_str);
      });

      setAdds(adds as never[]);
    }

    if (sectionToDraw.polylines!.hasOwnProperty("holes")) {
      sectionToDraw.polylines!.holes!.forEach((hole: ng.geometry.PolyLine2D) => {
        let hole_str = "";
        hole.points!.forEach((val, hindex) => {
          let valX = secSVG.origo.x + val.x * scale * zoomFactor;
          let valY = secSVG.origo.y - val.y * scale * zoomFactor;
          if (hindex === 0) {
            hole_str += `${valX},${valY} `;
            firstpoint = `${valX},${valY} `;
          } else {
            hole_str += `${valX},${valY} `;
          }
          hlp.push([valX, valY]);
        });

        hole_str += firstpoint;
        holes.push(hole_str);
      });
    }
    // https://youtu.be/Dzc2VWuBb2I?si=RT8NZiVTUaW3XbcH (jakob humour)
    setHoles(holes as never[]);

    setHelpPnts(hlp);

    // ------------------------------- (possibly) rotated values  -------------------------------------
    let rot = calcRot(props.rotVec ? props.rotVec : [1, 0]);

    scale = calcSecScale(sectionToDraw, rotSecSVG);

    firstpoint = "";
    holes = [];
    adds = [];

    points = sectionToDraw.polylines?.perimeter?.points as ng.geometry.IPoint2D[];
    per_str = "";
    points.forEach((val, index) => {
      let tmpX = val.x * Math.cos((rot * Math.PI) / 180) - val.y * Math.sin((rot * Math.PI) / 180);
      let tmpY = val.x * Math.sin((rot * Math.PI) / 180) + val.y * Math.cos((rot * Math.PI) / 180);

      let valX = rotSecSVG.origo.x + tmpX * scale * zoomFactor;
      let valY = rotSecSVG.origo.y - tmpY * scale * zoomFactor;
      if (index === 0) {
        per_str += `${valX},${valY} `;
        firstpoint = `${valX},${valY} `;
      } else {
        per_str += `${valX},${valY} `;
      }
    });

    per_str += firstpoint;
    setRotatedPolyLine(per_str);

    if (sectionToDraw.polylines!.hasOwnProperty("additionalPerimeters")) {
      // first move perimeter to the beginning of the additional perimeter list and remove it from the 'polyline'-list
      adds.push(per_str);
      setPolyLine("");

      sectionToDraw.polylines!.additionalPerimeters!.forEach((add: ng.geometry.PolyLine2D) => {
        let add_str = "";
        add.points!.forEach((val, hindex) => {
          let tmpX = val.x * Math.cos((rot * Math.PI) / 180) - val.y * Math.sin((rot * Math.PI) / 180);
          let tmpY = val.x * Math.sin((rot * Math.PI) / 180) + val.y * Math.cos((rot * Math.PI) / 180);

          let valX = rotSecSVG.origo.x + tmpX * scale * zoomFactor;
          let valY = rotSecSVG.origo.y - tmpY * scale * zoomFactor;
          if (hindex === 0) {
            add_str += `${valX},${valY} `;
            firstpoint = `${valX},${valY} `;
          } else {
            add_str += `${valX},${valY} `;
          }
        });

        add_str += firstpoint;
        adds.push(add_str);
      });

      setRotatedAdds(adds as never[]);
    }

    if (sectionToDraw.polylines!.hasOwnProperty("holes")) {
      sectionToDraw.polylines!.holes!.forEach((hole: ng.geometry.PolyLine2D) => {
        let hole_str = "";
        hole.points!.forEach((val, hindex) => {
          let tmpX = val.x * Math.cos((rot * Math.PI) / 180) - val.y * Math.sin((rot * Math.PI) / 180);
          let tmpY = val.x * Math.sin((rot * Math.PI) / 180) + val.y * Math.cos((rot * Math.PI) / 180);

          let valX = rotSecSVG.origo.x + tmpX * scale * zoomFactor;
          let valY = rotSecSVG.origo.y - tmpY * scale * zoomFactor;
          if (hindex === 0) {
            hole_str += `${valX},${valY} `;
            firstpoint = `${valX},${valY} `;
          } else {
            hole_str += `${valX},${valY} `;
          }
        });

        hole_str += firstpoint;
        holes.push(hole_str);
      });
    }

    setRotatedHoles(holes as never[]);
  };

  const Axes = () => {
    let cog = [0, 0];
    let ang1 = 0;
    let ang2 = Math.PI / 2;
    let shearCntr = [0, 0];
    let sec = props.section;

    if (sec && sec.units !== undefined && sec.units !== null) {
      Object.keys(sec.units).forEach((k, index) => {
        if (sec.units === null) return null;

        const unitVal: any = sec.units ? [k] : null;
        if (unitVal === null) return null;

        if (k === "cog") {
          if (typeof unitVal === "object" && unitVal !== null) {
            cog = [(unitVal as { x: number }).x * scale, (unitVal as { y: number }).y * scale];
          }
        }

        if (k === "ang1") {
          ang1 = Number(unitVal);
        }

        if (k === "ang2") {
          ang2 = Number(unitVal);
        }

        if (k === "xs") {
          shearCntr[0] = Number(unitVal) * scale;
        }

        if (k === "ys") {
          shearCntr[1] = Number(unitVal) * scale;
        }
      });
    }

    const midPnt = [secSVG.w / 2, secSVG.h / 2];

    const origo = [
      midPnt[0] + ((-secW * scale) / 2 + cog[0]) * zoomFactor,
      midPnt[1] + ((secH * scale) / 2 - cog[1]) * zoomFactor,
    ];

    const origoShear = [origo[0] + shearCntr[0] * zoomFactor, origo[1] - shearCntr[1] * zoomFactor];

    const yAxis_StartPnt = [0, origo[1]];
    const yAxis_EndPnt = [secSVG.w, origo[1]];

    const zAxis_StartPnt = [origo[0], 0];

    const zAxis_EndPnt = [origo[0], secSVG.h];

    let R = Math.sqrt(Math.pow(cog[0], 2) + Math.pow(cog[1], 2)) * 1.2 * zoomFactor;

    const _1Axis_StartPnt = [origo[0] - R * Math.cos(ang1), origo[1] + R * Math.sin(ang1)];
    const _1Axis_EndPnt = [origo[0] + R * Math.cos(ang1), origo[1] - R * Math.sin(ang1)];

    const _2Axis_StartPnt = [origo[0] - R * Math.cos(ang2), origo[1] + R * Math.sin(ang2)];
    const _2Axis_EndPnt = [origo[0] + R * Math.cos(ang2), origo[1] - R * Math.sin(ang2)];

    // let horMarg = secSVG.w * secSVG.pad;
    // let vertMarg = secSVG.h * secSVG.pad;

    // const xCoord = [
    //   (secSVG.w - horMarg).toString(),
    //   (horMarg).toString(),
    // ];
    // const yCoord = [
    //   (secSVG.h - vertMarg).toString(),
    //   (vertMarg).toString(),
    // ];

    // const xPnt = xCoord[0] + "," + yCoord[0];
    // const yPnt = xCoord[1] + "," + yCoord[1];

    // const xArrow = "l-10,-5 l0,10 l10,-5";
    // const yArrow = "l-5,10 l10,0 l-5,-10";

    const xTxt = [(yAxis_EndPnt[0] - 25).toString(), (yAxis_EndPnt[1] - 5).toString()];
    const yTxt = [(zAxis_StartPnt[0] - 25).toString(), (zAxis_StartPnt[1] + 25).toString()];

    const _1Txt = [_1Axis_StartPnt[0].toString(), _1Axis_StartPnt[1].toString()];

    const _2Txt = [_2Axis_StartPnt[0].toString(), _2Axis_StartPnt[1].toString()];

    let yAxis =
      "M" +
      yAxis_StartPnt[0].toString() +
      "," +
      yAxis_StartPnt[1].toString() +
      " " +
      "L" +
      yAxis_EndPnt[0].toString() +
      "," +
      yAxis_EndPnt[1].toString();

    //  +
    // " " +
    // xArrow;

    let zAxis =
      "M" +
      zAxis_StartPnt[0].toString() +
      "," +
      zAxis_StartPnt[1].toString() +
      " " +
      "L" +
      zAxis_EndPnt[0].toString() +
      "," +
      zAxis_EndPnt[1].toString();

    //  +
    // " " +
    // yArrow;

    let _1Axis =
      "M" +
      _1Axis_StartPnt[0].toString() +
      "," +
      _1Axis_StartPnt[1].toString() +
      " " +
      "L" +
      _1Axis_EndPnt[0].toString() +
      "," +
      _1Axis_EndPnt[1].toString();

    let _2Axis =
      "M" +
      _2Axis_StartPnt[0].toString() +
      "," +
      _2Axis_StartPnt[1].toString() +
      " " +
      "L" +
      _2Axis_EndPnt[0].toString() +
      "," +
      _2Axis_EndPnt[1].toString();

    let clrYaxis = "#d91e18";
    let clrZaxis = "#3c73a8";
    let clr1axis = "#d91e18";
    let clr2axis = "#3c73a8";
    let middleCircle = "#fffc7f";

    return (
      <g className="axisPaths">
        <path id="axis-1" fill={clr1axis} stroke={clr1axis} strokeWidth="1" d={_1Axis} />

        <text id="label-1" x={_1Txt[0]} y={_1Txt[1]} style={{ fontSize: "30px" }} stroke={clr1axis} fill={clr1axis}>
          1
        </text>

        <path id="axis-2" fill={clr2axis} stroke={clr2axis} strokeWidth="1" d={_2Axis} />

        <text id="label-2" x={_2Txt[0]} y={_2Txt[1]} style={{ fontSize: "30px" }} stroke={clr2axis} fill={clr2axis}>
          2
        </text>

        <path id="yp-axis" fill={clrYaxis} stroke={clrYaxis} strokeWidth="2" d={yAxis} />

        <text
          //  id="yp-label"
          id="label-3"
          x={xTxt[0]}
          y={xTxt[1]}
          style={{ fontSize: "30px" }}
          stroke={clrYaxis}
          fill={clrYaxis}
        >
          y'
        </text>

        <path id="Z-axis" fill={clrZaxis} stroke={clrZaxis} strokeWidth="2" d={zAxis} />

        <text
          //id="zp-label"
          id="label-4"
          x={yTxt[0]}
          y={yTxt[1]}
          style={{ fontSize: "30px" }}
          stroke={clrZaxis}
          fill={clrZaxis}
        >
          z'
        </text>

        <circle cx={origoShear[0]} cy={origoShear[1]} r="5" stroke="black" fill={middleCircle} />
      </g>
    );
  };

  // --------- other funcs ------------

  const calcSecScale = (sec: ng.sections.Section, svg: any) => {
    const perimeter: ng.geometry.IPolyLine2D = sec.polylines?.perimeter as ng.geometry.IPolyLine2D;
    let maxX: number = -1e6;
    let minX: number = 1e6;
    let maxY: number = -1e6;
    let minY: number = 1e6;

    let index: number = -1;
    let pnts: ng.geometry.IPoint2D[] = perimeter.points as ng.geometry.IPoint2D[];
    for (let pnt of pnts) {
      index++;
      let x: number = 0,
        y = 0;
      if (typeof pnt.x != "undefined") {
        x = pnt.x as number;
      }
      if (typeof pnt.y != "undefined") {
        y = pnt.y as number;
      }

      pnts[index].x = x;
      pnts[index].y = y;

      if (x > maxX) maxX = x;
      if (x < minX) minX = x;
      if (y > maxY) maxY = y;
      if (y < minY) minY = y;
    }

    if (sec.polylines!.hasOwnProperty("additionalPerimeters")) {
      sec.polylines!.additionalPerimeters!.forEach((add, i) => {
        let pnts: ng.geometry.IPoint2D[] = add.points as ng.geometry.IPoint2D[];
        let index = -1;
        for (let pnt of pnts) {
          index++;
          let x: number = 0,
            y = 0;
          if (typeof pnt.x != "undefined") {
            x = pnt.x as number;
          }
          if (typeof pnt.y != "undefined") {
            y = pnt.y as number;
          }

          pnts[index].x = x;
          pnts[index].y = y;

          if (x > maxX) maxX = x;
          if (x < minX) minX = x;
          if (y > maxY) maxY = y;
          if (y < minY) minY = y;
        }
      });
    }

    let dX = Math.abs(maxX - minX);
    let dY = Math.abs(maxY - minY);

    //-------------------    center the section ------------------------------------------------
    pnts = perimeter.points as ng.geometry.IPoint2D[];
    for (let pnt of pnts) {
      pnt.x! -= minX + dX / 2;
      pnt.y! -= minY + dY / 2;
    }

    if (sec.polylines!.hasOwnProperty("additionalPerimeters")) {
      sec.polylines!.additionalPerimeters!.forEach((add, index) => {
        let pnts: ng.geometry.IPoint2D[] = add.points as ng.geometry.IPoint2D[];
        for (let pnt of pnts) {
          pnt.x! -= minX + dX / 2;
          pnt.y! -= minY + dY / 2;
        }
      });
    }

    if (sec.polylines!.hasOwnProperty("holes")) {
      const holes: ng.geometry.IPolyLine2D[] = sec.polylines?.holes as ng.geometry.IPolyLine2D[];

      if (holes.length > 0) {
        // we assume that there will only be one hole in a section for now
        let pnts: ng.geometry.IPoint2D[] = holes[0].points as ng.geometry.IPoint2D[];
        for (let pnt of pnts) {
          pnt.x! -= minX + dX / 2;
          pnt.y! -= minY + dY / 2;
        }
      }
    }

    //----------------------------------------------------------------------

    setSecH(dY);
    setSecW(dX);

    // calculate radius
    let cog = [0, 0];

    if (sec && sec.units !== null) {
      Object.keys(sec.units).map((k, index) => {
        if (sec.units === null) return null;

        const unitVal = sec.units[k as keyof ng.sections.SectionUnits];

        if (k === "cog") {
          if (typeof unitVal === "object" && unitVal !== null) {
            cog = [unitVal.x, unitVal.y];
          }
        }
      });
    }

    let R = Math.sqrt(Math.pow(cog[0], 2) + Math.pow(cog[1], 2));

    let x = cog[0] - R;
    if (x < minX) minX = x;
    x = cog[0] + R;
    if (x > maxX) maxX = x;

    let y = cog[1] - R;
    if (y < minY) minY = y;
    y = cog[1] + R;
    if (y > maxY) maxY = y;

    dX = Math.abs(maxX - minX);
    dY = Math.abs(maxY - minY);

    const scaleW = svg.max.x / dX;
    const scaleH = svg.max.y / dY;

    const scale = Math.min(scaleW, scaleH);

    setScale(scale);

    return scale;
  };

  function calcRot(rotVec: [number, number]) {
    let angle = Math.atan2(rotVec[1], rotVec[0]);
    angle *= 180 / Math.PI;
    if (angle < 0) {
      angle += 360;
    }
    return angle;
  }

  const handleClick = (e: { detail: any }) => {
    switch (e.detail) {
      // singel click
      case 1:
        setShowAxes(!showAxes);
        break;
      // double click
      case 2:
        /* If we want to add any functionality on double click*/
        // setShowAxes(!showAxes)
        break;
    }
  };

  // ----------------- Render -------------------
  let renderPolyLine = props.rotation ? rotatedPolyLine : polyLine;
  let renderHoles = props.rotation ? rotatedHoles : holes;
  let renderAdds = props.rotation ? rotatedAdds : adds;
  const vbSec = "0 0 " + secSVG.w.toString() + " " + secSVG.h.toString();
  const vbRotSec = "0 0 " + rotSecSVG.w.toString() + " " + rotSecSVG.h.toString();

  // ------------------ see NGCustomSection.tsx for comments ------------------------

  var digits = hlpNbr.toString().split("");
  var arr = digits.map(Number);

  let hlpOrient = arr[0];
  let p1 = arr[1] * 10 + arr[2];
  let p2 = arr[3] * 10 + arr[4];

  let numOfLines = Math.max(Math.round(secH / 0.01), Math.round(secW / 0.01));

  // Generate lines using a loop
  const hlines = Array.from({ length: numOfLines }).map((_, index) => (
    <line
      key={index}
      x1={0}
      y1={(index + 1) * 0.1 * scale} // Y position of each line
      x2={secSVG.w}
      y2={(index + 1) * 0.1 * scale}
      style={{
        fill: "none",
        stroke: "lightblue",
        strokeWidth: 1,
      }}
    />
  ));
  const vlines = Array.from({ length: numOfLines }).map((_, index) => (
    <line
      key={index}
      x1={(index + 1) * 0.1 * scale}
      y1={0}
      x2={(index + 1) * 0.1 * scale}
      y2={secSVG.h}
      style={{
        fill: "none",
        stroke: "lightblue",
        strokeWidth: 1,
      }}
    />
  ));

  //----------------------------------------------------------------------

  return (
    <svg
      width="100%"
      height="100%"
      viewBox={props.viewbox ? props.viewbox : props.rotation ? vbRotSec : vbSec}
      preserveAspectRatio="xMidYMid"
      onClick={handleClick}
    >
      {grid && (
        <>
          {hlines}
          {vlines}
        </>
      )}

      <polyline
        points={renderPolyLine}
        style={{
          fill: mtrlFill,
          stroke: "black",
          strokeWidth: 1,
        }}
      />

      {renderAdds &&
        renderAdds.map((str, index) => {
          return (
            <polyline
              points={str}
              key={index}
              style={{
                fill: mtrlFill,
                stroke: "black",
                strokeWidth: 1,
              }}
            />
          );
        })}

      {renderHoles &&
        renderHoles.map((str, index) => {
          return (
            <polyline
              points={str}
              key={index}
              style={{
                fill: "white",
                stroke: "black",
                strokeWidth: 1,
              }}
            />
          );
        })}

      {/* see NGCustomSection.tsx for comments */}
      {helpPnts.length > 0 && (
        <>
          {hlpNbr && (
            <>
              {hlpOrient === 1 && ( // normal
                <line
                  x1={helpPnts[p1][0]}
                  y1={helpPnts[p1][1]}
                  x2={helpPnts[p2][0]}
                  y2={helpPnts[p2][1]}
                  style={{
                    fill: "none",
                    stroke: "red",
                    strokeWidth: 2,
                  }}
                />
              )}
              {hlpOrient === 2 && ( // horizontal
                <line
                  x1={helpPnts[p1][0]}
                  y1={helpPnts[p1][1]}
                  x2={helpPnts[p2][0]}
                  y2={helpPnts[p1][1]}
                  style={{
                    fill: "none",
                    stroke: "red",
                    strokeWidth: 2,
                  }}
                />
              )}
              {hlpOrient === 3 && ( // vertical
                <line
                  x1={helpPnts[p1][0]}
                  y1={helpPnts[p1][1]}
                  x2={helpPnts[p1][0]}
                  y2={helpPnts[p2][1]}
                  style={{
                    fill: "none",
                    stroke: "red",
                    strokeWidth: 2,
                  }}
                />
              )}
            </>
          )}
        </>
      )}

      {!grid && showAxes && !props.rotation && Axes()}
    </svg>
  );
};

export default SectionSVG;
